import React from 'react';
import SVG from 'react-inlinesvg';
import classNames from 'classnames';
import { __ } from '@wordpress/i18n';
import PropTypesConfig from 'config/prop-types';
import displayNumber from 'util/displayNumber';
import displayField from 'util/displayField';
import displayText from 'util/displayText';
import formatDate from 'util/formatDate';
import displayCandidateName from 'util/displayCandidateName';
import timeAgoDate from 'util/timeAgo';
import formatNumber from 'util/formatNumber';
import { ThemeProvider } from 'styled-components';

// Shared styles.
import cardStyles from 'styles/cards';
import helperStyles from 'styles/helpers';
import tableStyles from 'styles/table';
import theme from 'styles/theme';
import CheckIcon from 'styles/checkmark.svg';

// Styled components.
import styles from './styles';

/**
 * A component representing a results card for a race.
 */
export default class RaceCard extends React.PureComponent {
  /**
   * Set PropTypes for this component.
   * @type {object}
   */
  static propTypes = PropTypesConfig.RACE_SHAPE;

  /**
   * Set Initial State
   * @type {object}
   */
  state = {
    updatedDate: '',
  };

  /**
   * On initial mount set interval.
   */
  componentDidMount() {
    // Display updated Time on init.
    this.updatedTimer();

    // Set interval to fire every 60 seconds.
    this.updatedId = setInterval(this.updatedTimer.bind(this), 60 * 1000);
  }

  /**
   * Actions to fire after the component updated.
   * @param {object} prevProps - Previous props, before the update.
   */
  componentDidUpdate(prevProps) {
    const {
      last_updated: lastUpdated = 0,
    } = this.props;

    const {
      last_updated: lastUpdatedOld = 0,
    } = prevProps;

    // Only update last updated state if it's changed.
    if (lastUpdated !== lastUpdatedOld) {
      this.updatedTimer();
      clearInterval(this.updatedId);
    }
  }

  /**
   * On unmount, clear interval.
   */
  componentWillUnmount() {
    clearInterval(this.updatedId);
  }

  /**
   * Update Last Updated date.
   */
  updatedTimer() {
    const {
      last_updated: lastUpdated,
    } = this.props;

    this.setState({
      updatedDate: timeAgoDate(formatDate(lastUpdated)),
    });
  }

  /**
   * Render this component.
   * @returns {object} - JSX for this component.
   */
  render() {
    // Shared Styled Components.
    const {
      Card,
      GlobalStyles,
      HeaderWrap,
      ItemStatus,
      ItemTitle,
      SubTitle,
      Title,
    } = cardStyles;
    const {
      Table,
      TableCell,
      TableFooter,
      TableHeader,
      TableRow,
    } = tableStyles;
    const {
      ScreenReaderText,
    } = helperStyles;

    // Race Styled Components.
    const {
      CandidateImage,
      CandidateInfoWrap,
      CandidateParty,
    } = styles;

    const {
      candidates = [],
      precincts_percentage: precinctsPercentage,
      subtitle,
      suppress_updated: suppressUpdated = false,
      title,
    } = this.props;

    const {
      updatedDate,
    } = this.state;

    const display = {
      candidates: Array.isArray(candidates) && 0 < candidates.length,
      electoral: displayField(candidates, 'electoral_votes'),
      images: displayField(candidates, 'image_url'),
      lastUpdated: ! suppressUpdated,
      party: displayField(candidates, 'party_name'),
      percentage: displayField(candidates, 'votes_percentage'),
      reporting: displayNumber(precinctsPercentage),
      subtitle: displayText(subtitle),
      title: displayText(title),
      total: displayField(candidates, 'votes_total'),
    };

    if (display.candidates) {
      candidates.sort(
        (c1, c2) => (c2.votes_total || 0) - (c1.votes_total || 0)
          || (c1.last_name || '').localeCompare(c2.last_name || '')
          || (c1.first_name || '').localeCompare(c2.first_name || '')
      );
    }

    const hasImage = candidates
      .filter((candidate) => candidate.image_url);

    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Card
          className={classNames(
            'election-hub__card',
            'election-hub--race-card__candidates',
            {
              'election-hub--race-card__with-photos': hasImage.length,
              'election-hub--card__with-subtitle': display.subtitle,
            }
          )}
        >
          <Table>
            {(display.title || display.subtitle) && (
              <caption>
                {display.title && (
                  <Title className="election-hub__card--title">{title}</Title>
                )}
                {display.subtitle && (
                  <SubTitle className="election-hub__card--sub-title">
                    {subtitle}
                  </SubTitle>
                )}
              </caption>
            )}
            <thead>
              <tr className="election-hub__info-header">
                {display.candidates && (
                  <TableHeader scope="col" alignText="left">
                    {__('Candidate', 'election-hub')}
                  </TableHeader>
                )}
                {display.total && (
                  <TableHeader scope="col">
                    {__('Votes', 'election-hub')}
                  </TableHeader>
                )}
                {display.percentage && (
                  <TableHeader scope="col">
                    {__('Percent', 'election-hub')}
                  </TableHeader>
                )}
                {display.electoral && (
                  <TableHeader
                    className="party__electoral-votes--header"
                    scope="col"
                  >
                    {__('E.V.', 'election-hub')}
                  </TableHeader>
                )}
              </tr>
            </thead>
            <tbody>
              {display.candidates && (
                candidates.map((candidate) => {
                  const {
                    electoral_votes: electoralVotes = '',
                    full_name: fullName = '',
                    image_url: imageUrl = '',
                    party_name: partyName = '',
                    party_abbreviation: partyAbbreviation = '',
                    votes_percentage: votesPercentage = '',
                    votes_total: votesTotal = '',
                    winner = false,
                  } = candidate;

                  const generatedfullName = displayCandidateName(candidate);

                  return (
                    <TableRow
                      className={classNames(
                        {
                          [`party__${partyName.toLowerCase()}`]: display.party,
                          candidate__winner: winner,
                        }
                      )}
                      key={fullName}
                    >
                      <TableHeader scope="row" alignText="left">
                        <HeaderWrap
                          className={classNames(
                            {
                              'party__has-candidate-image': imageUrl,
                            }
                          )}
                        >
                          {display.images && (
                            <CandidateImage>
                              {imageUrl && (
                                <img
                                  src={imageUrl}
                                  alt=""
                                />
                              )}
                            </CandidateImage>
                          )}
                          <CandidateInfoWrap>
                            {null !== generatedfullName && (
                              <ItemTitle>
                                {null !== generatedfullName && [
                                  <span className="candidate__first-name">
                                    {`${generatedfullName.first} `}
                                  </span>,
                                  <span className="candidate__last-name">
                                    {generatedfullName.last}
                                  </span>,
                                ]}
                                {
                                  display.party
                                  && partyName
                                  && partyAbbreviation
                                  && (
                                    <CandidateParty>
                                      <span className="election-hub--desktop">
                                        {partyName}
                                      </span>
                                      <span className="election-hub--mobile">
                                        {partyAbbreviation}
                                      </span>
                                    </CandidateParty>
                                  )
                                }
                              </ItemTitle>
                            )}
                          </CandidateInfoWrap>
                          <ItemStatus type="race">
                            <ScreenReaderText>
                              {__('Race not called', 'election-hub')}
                            </ScreenReaderText>
                            <SVG src={CheckIcon} />
                          </ItemStatus>
                        </HeaderWrap>
                      </TableHeader>
                      {display.total && (
                        <TableCell>{formatNumber(votesTotal)}</TableCell>
                      )}
                      {display.percentage && (
                        <TableCell
                          className="election-hub__percentage"
                        >
                          {`${formatNumber(votesPercentage, 2)}%`}
                        </TableCell>
                      )}
                      {display.electoral && (
                        <TableCell className="party__electoral-votes">
                          {formatNumber(electoralVotes)}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5">
                  <TableFooter>
                    {display.lastUpdated && updatedDate && (
                      <div className="election-hub--last-updated">
                        {__('Updated: ', 'election-hub')}
                        {updatedDate}
                      </div>
                    )}
                    {display.reporting && (
                      <div className="election-hub--reporting">
                        {`${precinctsPercentage}% `}
                        {__('reporting', 'election-hub')}
                      </div>
                    )}
                  </TableFooter>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card>
      </ThemeProvider>
    );
  }
}
